import { Box, Chip, useTheme } from "@mui/material";
import { spacing, statusChipStyle, type StatusSlot } from "@aeriosiq/ui";
import { UmbrellaIcon, WrenchIcon } from "@aeriosiq/ui/icons";

import type { TimeTrackingActivityType } from "./types.js";

/**
 * TT-02 — the ☂ / ⚙ type pill from mockups A–C.
 *
 * D81's recipe, via the shared `statusChipStyle`: the hue owns the fill, the
 * keyline and — here — the ICON, and the label stays neutral. The icon replaces
 * the recipe's dot as the non-text signal (D26): a standby type is a KIND, not
 * a lifecycle state, and an umbrella says which kind at a glance in a way a
 * coloured dot never could.
 *
 * Both standby types sit in the `info` slot deliberately. Weather and technical
 * are not better or worse than one another — they are two categories of the
 * same neutral fact, and spending `attention` or `critical` on one of them
 * would import a severity the domain does not have. They are told apart by
 * glyph and label, which is what D26 asks for anyway.
 */
export interface StandbyTypeChipProps {
  activityType: TimeTrackingActivityType;
  /** Icon only — for the narrow "Type" column of the review table (mockup C). */
  iconOnly?: boolean;
  /** Suffix appended to the label, e.g. a duration: `"☂ Weather · 5.0 h"`. */
  suffix?: string;
  dense?: boolean;
}

const LABELS: Partial<Record<TimeTrackingActivityType, string>> = {
  standby_weather: "Weather",
  standby_technical: "Technical",
  standby: "Standby",
  travel: "Travel",
  onsite: "On site",
  prescreening: "Pre-screening",
  expert_review: "Expert review",
  report_generation: "Report",
  other: "Other",
};

export function standbyTypeLabel(
  activityType: TimeTrackingActivityType,
): string {
  return LABELS[activityType] ?? activityType;
}

/** Every activity type reads as an informational category — see the header. */
const SLOT: StatusSlot = "info";

export function StandbyTypeChip({
  activityType,
  iconOnly = false,
  suffix,
  dense = false,
}: StandbyTypeChipProps) {
  const theme = useTheme();
  const style = statusChipStyle(theme, SLOT);
  const label = standbyTypeLabel(activityType);
  const Glyph =
    activityType === "standby_technical" ? WrenchIcon : UmbrellaIcon;

  const text = iconOnly ? "" : suffix ? `${label} · ${suffix}` : label;

  return (
    <Chip
      label={text}
      size={dense ? "small" : "medium"}
      variant="filled"
      icon={
        <Box
          component="span"
          aria-hidden
          sx={{ display: "inline-flex", color: style.dotColor }}
        >
          <Glyph fontSize="small" />
        </Box>
      }
      sx={{
        backgroundColor: style.backgroundColor,
        color: style.color,
        flexShrink: 0,
        fontWeight: 500,
        "& .MuiChip-label": {
          color: style.color,
          ...(iconOnly ? { pr: 0.75 } : {}),
        },
        "& .MuiChip-icon": {
          marginInlineStart: spacing.sm,
          marginInlineEnd: 0,
        },
      }}
      data-activity-type={activityType}
      aria-label={`Type: ${label}${suffix ? ` (${suffix})` : ""}`}
    />
  );
}
